import { Analytics, EventProps } from "@faro-lotv/foreign-observers";
import { useState } from "react";
import { ReactSetStateFunction } from "@utils/utility-types";
import { AsyncUtils, DateTimeUtils } from "@stellar/utils";
import { RouteParams } from "@router/route-params";
import { useLocation } from "react-router-dom";
import { useAppParams } from "@router/router-helper";
import { LogEventParams } from "@utils/track-event/event-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { LimitValueProps } from "@utils/track-event/event-types";

/** All the ids available in the app */
const allEntityIds: Array<keyof RouteParams> = [
  "companyId",
  "projectId",
  "memberId",
  "groupId",
];

/** Time to wait for the analytics module to initialize, if not happened before trying to track an event */
const initWaitingTimeInMS = DateTimeUtils.MILLISECONDS_IN_SECOND;

/** Default properties for the event */
interface DefaultProps {
  /** Company id */
  companyId?: string;

  /** project id */
  projectId?: string;
}

interface UseTrackEvent {
  /**
   * Function that tracks an event in the background, returning immediately.
   * In most cases, this should be preferred over `trackAsyncEvent`.
   */
  trackEvent(event: LogEventParams): void;

  /**
   * Function that tracks an event, returning a Promise that waits for the result.
   */
  trackAsyncEvent(event: LogEventParams): Promise<void>;

  /**
   * Function to set if the Analytics module is initialized,
   * until it is set to true the events will be delayed.
   */
  setIsAnalyticsModuleInitialized: ReactSetStateFunction<boolean>;

  /**
   * Converts the limits to event props
   * @param limits - The limits to convert
   */
  limitsToEventProp(limits: FaroCoreApiTypes.LimitPayload[]): EventProps;
}

export function useTrackEvent(): UseTrackEvent {
  const { pathname } = useLocation();
  const { projectId, companyId } = useAppParams();
  const params = useAppParams();
  const [isAnalyticsModuleInitialized, setIsAnalyticsModuleInitialized] =
    useState<boolean>(false);

  /**
   * Gets the props for the event
   */
  function getProps(props?: EventProps): EventProps {
    const defaultProps: DefaultProps = {};

    // override the default projectId if the projectId is provided in props
    if (projectId && !props?.projectId) {
      defaultProps.projectId = projectId;
    }

    // override the default projectId if the projectId is provided in props
    if (companyId && !props?.companyId) {
      defaultProps.companyId = companyId;
    }

    const allProps: EventProps = {
      ...defaultProps,
      ...props,
      path: createPathForTracking(pathname, params),
    };

    return allProps;
  }

  /**
   * Tracks an event
   * @param param0 - The event parameters
   */
  function trackEvent(event: LogEventParams): void {
    const props = "props" in event ? event.props : undefined;

    // If the Analytics module is not initialized, we wait for a bit and then send the event
    if (isAnalyticsModuleInitialized) {
      Analytics.track(event.name, getProps(props));
    } else {
      setTimeout(() => {
        Analytics.track(event.name, getProps(props));
      }, initWaitingTimeInMS);
    }
  }

  /**
   * Function that tracks an event, returning a Promise that waits for the result.
   * @param param0 - The event parameters
   */
  async function trackAsyncEvent(event: LogEventParams): Promise<void> {
    const props = "props" in event ? event.props : undefined;

    // If the Analytics module is not initialized, we wait for a bit and then send the event
    if (!isAnalyticsModuleInitialized) {
      await AsyncUtils.delay(initWaitingTimeInMS);
    }

    await Analytics.track(event.name, getProps(props));
  }

  return {
    trackEvent,
    trackAsyncEvent,
    setIsAnalyticsModuleInitialized,
    limitsToEventProp,
  };
}

/** Converts the limits to event props, with the value of each limit keyed by its type */
export function limitsToEventProp(
  limits: FaroCoreApiTypes.LimitPayload[]
): EventProps {
  return limits.reduce<LimitValueProps>((acc, limit) => {
    acc[limit.type] = limit.value;

    // The 2026 plans limit the active users through ACTIVE_SEAT instead of USER_ROLE. The value is
    // also tracked as USER_ROLE, so that the charts of the active users keep covering every plan
    if (limit.type === "ACTIVE_SEAT") {
      acc.USER_ROLE = limit.value;
    }

    return acc;
  }, {});
}

/**
 * Creates the path parameter for tracking by replacing the actual entityIds with its name
 * For example the ID of company changes to "companyId" in the pathname
 * encodeURIComponent is used to replaces the encoded dynamicId with staticId in the pathname.
 * If memberId is foo@bar in the pathname it becomes foo%40bar and need to be encoded to be replaced with memberId
 */
export function createPathForTracking(
  originalPathname: string,
  params: Readonly<Partial<RouteParams>>
): string {
  try {
    let replacedPath = originalPathname;
    for (const [paramName, paramValue] of Object.entries(params)) {
      if (allEntityIds.includes(paramName as keyof RouteParams)) {
        // Replaces the encoded dynamicId with staticId in the pathname.
        // This is used as some characters are encoded in the pathname, e.g. foo@bar -> foo%40bar
        replacedPath = replacedPath.replace(
          encodeURIComponent(paramValue),
          paramName
        );
      }
    }
    return replacedPath;
  } catch (error) {
    // If there is an error in the function, log the error and return an empty string
    // Therefore the app doesn't crash and no sensitive data like project id is sent to the analytic service
    // eslint-disable-next-line no-console
    console.error("Error in createPathForTracking", error);
    return "";
  }
}
